import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://panchi.world';
const DEFAULT_OG_IMAGE = 'https://panchi.world/og-image.jpg';

// Comprehensive page-specific SEO data
const PAGE_SEO = {
  '/': {
    title: 'Panchi – AI Travel Planner for India | Smarter than MakeMyTrip',
    description: 'Panchi is India\'s smartest AI travel planner. Build personalized itineraries, find travel buddies, and plan trips better than MakeMyTrip — powered by AI.',
    keywords: 'AI travel planner, MakeMyTrip alternative, trip planner India, travel buddy finder, AI itinerary generator, best travel app India',
    ogTitle: 'Panchi – AI Travel Planner for India',
    ogDescription: 'Plan smarter trips with AI. Personalized itineraries, travel buddy finder, and more.',
  },
  '/planning': {
    title: 'Plan Your Trip with AI | Free Itinerary Generator | Panchi',
    description: 'Create your perfect travel itinerary in seconds with AI. Enter destination, budget & travel style - get personalized day-by-day plans. Better than MakeMyTrip!',
    keywords: 'AI trip planner, itinerary generator, travel planning, vacation planner, trip itinerary, Rajasthan itinerary, Goa trip planner',
    ogTitle: 'AI Trip Planner - Create Perfect Itineraries',
    ogDescription: 'Let AI plan your entire trip in seconds. Personalized itineraries for any destination.',
  },
  '/community': {
    title: 'Find Travel Buddies India | Solo Travel Community | Panchi',
    description: 'Connect with verified Indian travelers. Find travel buddies for your next trip, join group adventures, share travel plans. Safe community for solo travelers.',
    keywords: 'find travel buddy India, travel partner, solo travel community, group travel, travel companion finder, backpacking buddies India',
    ogTitle: 'Find Travel Buddies - Join the Community',
    ogDescription: 'Connect with fellow travelers. Find travel buddies for your next adventure.',
  },
  '/transport': {
    title: 'Compare Flights & Trains India | Best Deals | Panchi',
    description: 'Compare flight and train prices across MakeMyTrip, Cleartrip, ixigo & more. Find the cheapest transport options for your trip across India.',
    keywords: 'cheap flights India, train booking, bus booking, flight comparison India, IRCTC alternative, MakeMyTrip flights',
    ogTitle: 'Compare Flights & Transport - Best Deals',
    ogDescription: 'Find the cheapest flights, trains, and buses across India.',
  },
  '/hotels': {
    title: 'Compare Hotel Prices India | Best Deals | Panchi',
    description: 'Find the best hotel deals in India by comparing prices across Booking.com, Agoda, MakeMyTrip, OYO & more. Budget to luxury - all in one place.',
    keywords: 'hotel comparison India, cheap hotels, OYO alternative, hotel booking, resort deals India, budget hotels',
    ogTitle: 'Compare Hotels - Best Prices Guaranteed',
    ogDescription: 'Compare hotel prices across all major platforms. Find the best deals.',
  },
  '/about': {
    title: 'About Panchi | AI-Powered Travel Planning for India',
    description: 'Panchi is built for Indian travelers. Our AI understands local destinations, budgets, and travel styles. Join 10,000+ travelers planning smarter trips.',
    keywords: 'about panchi, AI travel app India, travel planning app, smart travel, travel technology startup',
    ogTitle: 'About Panchi - AI Travel Planner',
    ogDescription: 'Learn how Panchi is revolutionizing travel planning in India with AI.',
  },
  '/blog': {
    title: 'Travel Blog | Tips, Itineraries & Guides | Panchi',
    description: 'Discover AI-crafted travel guides, detailed itineraries for Rajasthan, Goa, Kerala & more. Expert tips for budget travel, solo trips, and hidden gems in India.',
    keywords: 'travel blog India, travel guides, Rajasthan itinerary, Goa travel guide, Kerala backwaters, budget travel tips',
    ogTitle: 'Panchi Travel Blog - Guides & Itineraries',
    ogDescription: 'Expert travel guides and AI-crafted itineraries for destinations across India.',
  },
  '/blog/rajasthan-5-day-itinerary': {
    title: 'Best 5-Day Rajasthan Itinerary by AI | Jaipur, Udaipur, Jodhpur | Panchi',
    description: 'Perfect 5-day Rajasthan itinerary covering Jaipur, Udaipur, and Jodhpur. AI-optimized route with budget tips, best hotels, and hidden gems. Plan your royal adventure!',
    keywords: 'Rajasthan itinerary 5 days, Jaipur Udaipur Jodhpur trip, Rajasthan travel guide, royal Rajasthan tour',
    ogTitle: '5-Day Rajasthan Itinerary - AI Planned',
    ogDescription: 'The perfect royal Rajasthan adventure covering Jaipur, Udaipur & Jodhpur.',
  },
  '/blog/goa-budget-trip': {
    title: 'How to Plan a Goa Trip on Budget Using AI | Under ₹15,000 | Panchi',
    description: 'Complete Goa budget travel guide. Plan an amazing Goa trip under ₹15,000 including flights, stays, food & activities. AI-optimized itinerary for beach lovers.',
    keywords: 'Goa budget trip, cheap Goa vacation, Goa travel guide, beach vacation India, Goa itinerary budget',
    ogTitle: 'Budget Goa Trip Guide - Under ₹15,000',
    ogDescription: 'Plan an incredible Goa vacation without breaking the bank. AI-optimized budget guide.',
  },
  '/blog/panchi-vs-makemytrip': {
    title: 'Why Panchi is Better than MakeMyTrip for Trip Planning | Comparison',
    description: 'Detailed comparison: Panchi AI vs MakeMyTrip. See why travelers prefer AI-powered planning over traditional booking. Personalized itineraries, better prices, travel buddies.',
    keywords: 'Panchi vs MakeMyTrip, best travel app India, AI travel planner comparison, MakeMyTrip alternative',
    ogTitle: 'Panchi vs MakeMyTrip - Which is Better?',
    ogDescription: 'Why smart travelers are switching from MakeMyTrip to AI-powered Panchi.',
  },
  '/find-travel-buddy': {
    title: 'Find Travel Buddy India | Safe Solo Travel Companion Finder | Panchi',
    description: 'Find your perfect travel companion for any destination in India. AI-powered matching based on travel style, budget & interests. Anonymous, safe, and free. Join 10,000+ travelers on Panchi.',
    keywords: 'find travel buddy India, travel companion finder, solo travel partner, travel buddy app, backpacking buddy India, Goa travel buddy, Ladakh travel partner, safe travel companion',
    ogTitle: 'Find Your Perfect Travel Buddy - AI Matched',
    ogDescription: 'Connect with like-minded travelers heading to the same destination. Safe, anonymous matching powered by AI.',
  },
  '/auth': {
    title: 'Sign In to Panchi | Start Planning Your Trip',
    description: 'Sign in or create a free Panchi account. Save trips, find travel buddies, and unlock AI-powered travel planning features.',
    keywords: 'panchi login, panchi sign up, travel app login',
    noIndex: false,
  },
  '/support': {
    title: 'Help & Support | Panchi',
    description: 'Get help with Panchi. Contact our support team, read FAQs, or report issues. We\'re here to make your travel planning seamless.',
    keywords: 'panchi support, travel help, customer service, FAQs',
  },
  '/privacy': {
    title: 'Privacy Policy | Panchi',
    description: 'Panchi privacy policy. Learn how we collect, use, and protect your personal data. Your privacy matters to us.',
    keywords: 'panchi privacy, data protection, privacy policy',
  },
  '/terms': {
    title: 'Terms of Service | Panchi',
    description: 'Panchi terms of service and user agreement. Read our terms before using the platform.',
    keywords: 'panchi terms, user agreement, terms of service',
  },
  // Private pages - noIndex
  '/saved': { title: 'My Saved Trips | Panchi', description: 'View and manage your saved travel plans.', noIndex: true },
  '/profile': { title: 'My Profile | Panchi', description: 'Manage your Panchi profile and travel preferences.', noIndex: true },
  '/onboarding': { title: 'Set Up Your Profile | Panchi', description: 'Tell us your travel preferences.', noIndex: true },
  '/notifications': { title: 'Notifications | Panchi', description: 'Your travel notifications.', noIndex: true },
  '/result': { title: 'Your AI Trip Plan | Panchi', description: 'View your personalized itinerary.', noIndex: true },
  '/payment': { title: 'Upgrade to Pro | Panchi', description: 'Unlock premium features.', noIndex: true },
  '/admin': { title: 'Admin Dashboard | Panchi', description: 'Admin panel.', noIndex: true },
  '/admin-login': { title: 'Admin Login | Panchi', description: 'Admin login.', noIndex: true },
};

// JSON-LD Structured Data for different page types
const getStructuredData = (path, seo) => {
  if (path === '/') {
    return {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Panchi",
      "url": "https://panchi.world",
      "applicationCategory": "TravelApplication",
      "operatingSystem": "Web, Android, iOS",
      "description": "AI-powered travel planning app for India. Build itineraries, find travel companions, and plan smarter trips.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "2500"
      }
    };
  }
  
  if (path.startsWith('/blog/') && path !== '/blog') {
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": seo.title,
      "description": seo.description,
      "author": {
        "@type": "Organization",
        "name": "Panchi Travel"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Panchi",
        "logo": {
          "@type": "ImageObject",
          "url": "https://panchi.world/favicon.png"
        }
      },
      "datePublished": "2026-03-01",
      "dateModified": "2026-03-28",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${BASE_URL}${path}`
      }
    };
  }
  
  if (path === '/find-travel-buddy') {
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Find Travel Buddy India",
      "url": "https://panchi.world/find-travel-buddy",
      "description": "AI-powered travel companion finder for solo travelers in India. Safe, anonymous matching based on travel preferences.",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Panchi",
        "url": "https://panchi.world"
      },
      "mainEntity": {
        "@type": "Service",
        "name": "Panchi Travel Buddy Finder",
        "description": "Find like-minded travel companions with AI-powered matching",
        "provider": {
          "@type": "Organization",
          "name": "Panchi"
        },
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "INR"
        }
      }
    };
  }
  
  if (path === '/blog') {
    return {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Panchi Travel Blog",
      "description": "Travel guides, itineraries, and tips for exploring India",
      "url": "https://panchi.world/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Panchi"
      }
    };
  }
  
  return null;
};

export default function SEO() {
  const location = useLocation();
  const path = location.pathname;
  
  // Handle dynamic paths
  const basePath = path.split('?')[0];
  
  // Get page-specific SEO or use defaults
  // If no match found and not a known route, mark as noIndex (404 pages)
  const knownPrefixes = ['/', '/planning', '/result', '/find-travel-buddy', '/community', '/transport', '/hotels', '/blog', '/auth', '/profile', '/saved', '/onboarding', '/notifications', '/payment', '/about', '/support', '/privacy', '/terms', '/admin'];
  const isKnownRoute = knownPrefixes.some(p => basePath === p || basePath.startsWith(p + '/'));
  
  const seo = PAGE_SEO[basePath] || {
    title: isKnownRoute 
      ? 'Panchi – AI Travel Planner for India | Plan Trips & Find Travel Buddies'
      : 'Page Not Found | Panchi',
    description: isKnownRoute
      ? 'Your AI-powered travel companion for personalized itineraries and finding travel buddies across India.'
      : 'The page you are looking for does not exist.',
    keywords: 'AI travel, trip planner, travel buddy India',
    noIndex: !isKnownRoute,
  };
  
  const canonicalUrl = `${BASE_URL}${basePath === '/' ? '' : basePath}`;
  const robotsContent = seo.noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';
  const structuredData = getStructuredData(basePath, seo);
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seo.title}</title>
      <meta name="title" content={seo.title} />
      <meta name="description" content={seo.description} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Panchi" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={seo.ogTitle || seo.title} />
      <meta property="og:description" content={seo.ogDescription || seo.description} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@panchitravel" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={seo.ogTitle || seo.title} />
      <meta name="twitter:description" content={seo.ogDescription || seo.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
